/**
 * Resolvers for the lw @wordpress/data store.
 *
 * Each resolver name matches a selector. @wordpress/data calls the resolver
 * automatically the first time the matching selector is invoked, then marks
 * it as resolved so subsequent calls hit the cache.
 *
 * @package LiquidWeb\Harbor
 */
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import { HarborError, ErrorCode } from '@/errors';
import type { Feature, LegacyLicense, ProductCatalog, License } from '@/types/api';
import type { Thunk } from './types';
import { forwardResolver, forwardResolverWithoutArgs } from '@/lib/forward-resolver';

/**
 * Fetches all features from the REST API and stores them.
 * Triggered automatically when getFeatures is first called.
 */
export const getFeatures =
	(): Thunk =>
	async ({ dispatch }) => {
		try {
			const features = await apiFetch<Feature[]>({
				path: '/liquidweb/harbor/v1/features',
			});
			dispatch.receiveFeatures(features);
		} catch (err) {
			throw await HarborError.wrap(
				err,
				ErrorCode.FeaturesFetchFailed,
				__('Nexcess Licensing failed to load your features.', '%TEXTDOMAIN%')
			);
		}
	};

/**
 * Fetches the active Harbor host plugin basenames from the REST API.
 * Triggered automatically when getHarborHostBasenames is first called, and
 * invalidated after plugin activation so the list stays current.
 */
export const getHarborHostBasenames =
	(): Thunk =>
	async ({ dispatch }) => {
		try {
			const basenames = await apiFetch<string[]>({
				path: '/liquidweb/harbor/v1/hosts',
			});
			dispatch.receiveHarborHosts(basenames);
		} catch (err) {
			throw await HarborError.wrap(
				err,
				ErrorCode.FeaturesFetchFailed,
				__('Nexcess Licensing failed to load Harbor hosts.', '%TEXTDOMAIN%')
			);
		}
	};

export const getFeaturesByProduct = forwardResolverWithoutArgs('getFeatures');
export const getFeature = forwardResolverWithoutArgs('getFeatures');
export const isFeatureEnabled = forwardResolverWithoutArgs('getFeatures');

// ---------------------------------------------------------------------------
// Legacy licenses
// ---------------------------------------------------------------------------

/**
 * Fetches legacy licenses from the REST API.
 */
export const getLegacyLicenses =
	(): Thunk =>
	async ({ dispatch }) => {
		try {
			const licenses = await apiFetch<LegacyLicense[]>({
				path: '/liquidweb/harbor/v1/legacy-licenses',
			});
			dispatch.receiveLegacyLicenses(licenses);
		} catch (err) {
			throw await HarborError.wrap(
				err,
				ErrorCode.LegacyLicensesFetchFailed,
				__('Nexcess Licensing failed to load legacy licenses.', '%TEXTDOMAIN%')
			);
		}
	};

export const getLegacyLicenseBySlug = forwardResolverWithoutArgs('getLegacyLicenses');
export const hasLegacyLicense = forwardResolverWithoutArgs('getLegacyLicenses');
export const hasLegacyLicenses = forwardResolver('getLegacyLicenses');

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

/**
 * Fetches all product catalogs from the REST API and stores them.
 * Triggered automatically when getCatalog is first called.
 */
export const getCatalog =
	(): Thunk =>
	async ({ dispatch }) => {
		try {
			const catalogs = await apiFetch<ProductCatalog[]>({
				path: '/liquidweb/harbor/v1/catalog',
			});
			dispatch.receiveCatalog(catalogs);
		} catch (err) {
			throw await HarborError.wrap(
				err,
				ErrorCode.CatalogFetchFailed,
				__('Nexcess Licensing failed to load the product catalog.', '%TEXTDOMAIN%')
			);
		}
	};

export const getProductCatalog = forwardResolverWithoutArgs('getCatalog');
export const getProductTiers   = forwardResolverWithoutArgs('getCatalog');
export const getCatalogTier    = forwardResolverWithoutArgs('getCatalog');

// ---------------------------------------------------------------------------
// License
// ---------------------------------------------------------------------------

/**
 * Fetches the stored license from the REST API.
 * Triggered automatically when getLicenseKey is first called.
 */
export const getLicenseKey =
	(): Thunk =>
	async ({ dispatch }) => {
		try {
			const result = await apiFetch<License>({
				path: '/liquidweb/harbor/v1/license',
			});
			dispatch.receiveLicense(result);
		} catch (err) {
			throw await HarborError.wrap(
				err,
				ErrorCode.LicenseFetchFailed,
				__('Nexcess Licensing failed to load your license.', '%TEXTDOMAIN%')
			);
		}
	};

export const hasLicense         = forwardResolver( 'getLicenseKey' );
export const getLicenseProducts = forwardResolverWithoutArgs( 'getLicenseKey' );
